import { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import FormInput from "../../../components/Form/FormInput";
import FormSelect from "../../../components/Form/FormSelect";
import FormTextarea from "../../../components/Form/TextArea";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { previewTotals } from "../../../utils/adjustments";

const RESOURCES = {
  receivable: {
    path: (type) => (type === "unit_based" ? "unit-based-receivables" : "flat-rate-receivables"),
    title: "Receivable",
    partyLabel: "Project client",
    noteLabel: "Note for client",
    module: MODULES.RECEIVABLES,
  },
  payable: {
    path: (type) => (type === "unit_based" ? "unit-based-payables" : "flat-rate-payables"),
    title: "Payable",
    partyLabel: "Job vendor",
    noteLabel: "Note for vendor",
    module: MODULES.PAYABLES,
  },
  "client-invoice": {
    path: () => "client-invoices",
    title: "Invoice",
    partyLabel: "Client",
    noteLabel: "Note for client",
    module: MODULES.CLIENT_INVOICES,
    invoice: true,
  },
  "vendor-invoice": {
    path: () => "vendor-invoices",
    title: "Invoice",
    partyLabel: "Vendor",
    noteLabel: "Note for vendor",
    module: MODULES.VENDOR_INVOICES,
    invoice: true,
  },
};

const LINE_TABS = [
  { key: "extra_charge", label: "Extra charge" },
  { key: "discount", label: "Discount" },
];
// Deductions only apply once the work is invoiced.
const INVOICE_TABS = [...LINE_TABS, { key: "deduction", label: "Deduction" }];

const TYPE_OPTIONS = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed", label: "Fixed" },
];

const fmtNum = (v) => Number(v || 0).toFixed(2);

const toForm = (saved) => ({
  type: saved?.type || "percentage",
  value: saved?.value ?? "",
  note: saved?.note || "",
  internal_note: saved?.internal_note || "",
});

const Field = ({ label, children }) => (
  <div className="flex">
    <span className="w-52 font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-gray-900">{children ?? "—"}</span>
  </div>
);

const AdjustmentsPage = ({ resource }) => {
  const config = RESOURCES[resource];
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lineType = searchParams.get("type") || "flat_rate";
  const tabs = config.invoice ? INVOICE_TABS : LINE_TABS;
  const activeTab = tabs.find((t) => t.key === searchParams.get("tab"))?.key ?? "extra_charge";
  const activeLabel = tabs.find((t) => t.key === activeTab).label;
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, config.module);

  const endpoint = `/project-finances/${config.path(lineType)}/${id}/adjustments`;

  const [data, setData] = useState(null);
  const [forms, setForms] = useState({ extra_charge: toForm(), discount: toForm(), deduction: toForm() });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const applyData = useCallback((next) => {
    setData(next);
    setForms({
      extra_charge: toForm(next.extra_charge),
      discount: toForm(next.discount),
      deduction: toForm(next.deduction),
    });
    setErrors({});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(endpoint, { withCredentials: true });
      applyData(res.data.data);
    } catch (err) {
      console.error(err);
      setLoadError(err.response?.data?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [endpoint, applyData]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (loadError)
    return <div className="text-center mt-10 text-danger-600 font-medium">{loadError}</div>;
  if (!data) return null;

  const form = forms[activeTab];
  const saved = data[activeTab];
  const disabled = readOnly || !!data.locked || saving;
  const currency = data.currency || "—";

  // The tab being edited uses its draft; the others what is saved.
  const draft = (kind) => (kind === activeTab ? form : data[kind]);
  const preview = previewTotals(
    data.subtotal,
    draft("extra_charge") || {},
    draft("discount") || {},
    draft("deduction") || {},
    data.taxes
  );

  const setField = (e) => {
    const { name, value } = e.target;
    setForms((prev) => ({ ...prev, [activeTab]: { ...prev[activeTab], [name]: value } }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    const value = Number(form.value);
    if (form.value === "" || !Number.isFinite(value) || value <= 0)
      next.value = "Enter a value greater than 0.";
    else if (activeTab !== "extra_charge" && form.type === "percentage" && value > 100)
      next.value = `A percentage ${activeLabel.toLowerCase()} cannot be more than 100%.`;
    else if (preview.beforeTaxes < 0)
      next.value = config.invoice
        ? "The discount and deduction together cannot be more than the subtotal plus the extra charge."
        : "The discount cannot be more than the subtotal plus the extra charge.";
    setErrors(next);
    return !Object.keys(next).length;
  };

  const submit = async (body, message) => {
    setSaving(true);
    try {
      const res = await api.put(endpoint, { kind: activeTab, ...body }, { withCredentials: true });
      applyData(res.data.data);
      toast.success(message);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!validate()) return;
    submit(
      { type: form.type, value: Number(form.value), note: form.note, internal_note: form.internal_note },
      `${activeLabel} saved.`
    );
  };

  const handleRemove = async () => {
    const label = activeLabel.toLowerCase();
    if (!(await confirmDialog(`Remove the ${label}?`))) return;
    submit({ type: null }, `The ${label} was removed.`);
  };

  const partyLink =
    data.party_id && (data.party === "client" ? `/clients/${data.party_id}` : `/vendors/${data.party_id}`);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <BackButton />
        <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Price adjustments: {config.title} {data.code}
        </h2>
      </div>

      {data.locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          {data.locked}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-8 space-y-4 text-sm">
        <Field label={config.partyLabel}>
          {partyLink ? (
            <Link to={partyLink} className="text-brand-600 hover:text-brand-800 underline">
              {data.party_name || "—"}
            </Link>
          ) : (
            data.party_name
          )}
        </Field>
        <Field label="Service">{data.service}</Field>
        <Field label="Language pair">{data.language_pair}</Field>
        <Field label="Project specialization">{data.specialization}</Field>
        <Field label="Subtotal">{fmtNum(data.subtotal)}</Field>
        <Field label="Currency">{data.currency}</Field>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="flex gap-6 px-8 pt-4 border-b border-gray-100">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pb-2 text-sm border-b-2 transition whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-brand-600 text-brand-600"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
              }`}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set("tab", tab.key);
                setSearchParams(next, { replace: true });
                setErrors({});
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid gap-8 p-8 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-5 max-w-xl">
            <FormSelect
              label={`${activeLabel} type`}
              name="type"
              value={form.type}
              onChange={setField}
              options={TYPE_OPTIONS}
              required
              disabled={disabled}
            />
            <FormInput
              label={`${activeLabel} value`}
              name="value"
              type="number"
              value={form.value}
              onChange={setField}
              error={errors.value}
              required
              disabled={disabled}
              suffix={form.type === "percentage" ? "%" : currency}
            />
            <FormTextarea
              label={config.noteLabel}
              name="note"
              value={form.note}
              onChange={setField}
              disabled={disabled}
            />
            <FormTextarea
              label="Internal note"
              name="internal_note"
              value={form.internal_note}
              onChange={setField}
              disabled={disabled}
            />

            {!readOnly && !data.locked && (
              <div className="flex gap-3">
                <button onClick={handleSave} disabled={saving} className={btn("primary", "sm")}>
                  Save
                </button>
                {saved?.type && (
                  <button onClick={handleRemove} disabled={saving} className={btn("danger", "sm")}>
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 p-5 text-sm space-y-2 self-start">
            <SummaryRow label="Subtotal" value={fmtNum(data.subtotal)} />
            <SummaryRow label="Extra charge" value={fmtNum(preview.extra)} />
            <SummaryRow label="Discount" value={`-${fmtNum(preview.discount)}`} />
            {config.invoice ? (
              <>
                <SummaryRow label="Deduction" value={`-${fmtNum(preview.deduction)}`} />
                <SummaryRow label="Total (before taxes)" value={fmtNum(preview.beforeTaxes)} />
                {preview.taxLines.map((t) => (
                  <SummaryRow
                    key={t.id}
                    label={t.type === "percentage" ? `${t.name} ${Number(t.rate)}%` : t.name}
                    value={fmtNum(t.amount)}
                  />
                ))}
                <SummaryRow label="Total" value={`${fmtNum(preview.total)} ${currency}`} strong />
              </>
            ) : (
              <SummaryRow label="Total" value={`${fmtNum(preview.beforeTaxes)} ${currency}`} strong />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, strong }) => (
  <div className={`flex justify-between gap-4 ${strong ? "border-t border-gray-200 pt-2 font-semibold text-gray-900" : "text-gray-700"}`}>
    <span>{label}</span>
    <span className="whitespace-nowrap">{value}</span>
  </div>
);

export default AdjustmentsPage;
